import { ChevronDown, Plus, X } from 'lucide-react';
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@design-system';
import type { EditableReply, ReplyCatalog, ReplyVersion } from '../api/api';

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });

/** What most edits never need: the title, the example emails, who gets unanswered emails, history. */
export function MoreSettings(props: {
  reply: EditableReply;
  owners: ReplyCatalog['owners'];
  versions: ReplyVersion[];
  publishedVersion: number | null;
  change: (patch: Partial<EditableReply>) => void;
  loadVersion: (version: ReplyVersion) => void;
}) {
  const { reply, change } = props;
  const setExample = (index: number, value: string) =>
    change({
      exampleEmails: reply.exampleEmails.map((example, i) => (i === index ? value : example)),
    });

  return (
    <Collapsible className="rounded-xl border border-zinc-200 bg-white px-5">
      <CollapsibleTrigger className="group flex min-h-[52px] w-full items-center justify-between text-[15px] font-semibold">
        More settings
        <ChevronDown
          className="size-4 transition-transform group-data-[state=open]:rotate-180"
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="flex flex-col gap-5 pb-5">
        <div className="flex flex-col gap-1.5">
          <Label
            htmlFor="reply-title"
            className="text-sm font-semibold"
          >
            Title
          </Label>
          <Input
            id="reply-title"
            value={reply.title}
            onChange={event => change({ title: event.target.value })}
            className="h-11"
          />
          <p className="text-[13px] text-zinc-600">
            How this reply is listed for the team. Advisors never see it.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Emails this reply answers</h3>
          <p className="text-[13px] text-zinc-600">
            The assistant compares each new email with these examples to decide whether this reply
            fits. Add at least three, worded the way advisors write; it reads the first six.
          </p>
          {reply.exampleEmails.map((example, index) => (
            <div
              key={index}
              className="flex items-center gap-2"
            >
              <Input
                aria-label={`Example ${index + 1}`}
                value={example}
                onChange={event => setExample(index, event.target.value)}
                className="h-11 bg-zinc-50"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label={`Remove example ${index + 1}`}
                disabled={reply.exampleEmails.length === 1}
                onClick={() =>
                  change({ exampleEmails: reply.exampleEmails.filter((_, i) => i !== index) })
                }
              >
                <X aria-hidden />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="h-11 self-start border-dashed"
            disabled={reply.exampleEmails.length >= 20}
            onClick={() => change({ exampleEmails: [...reply.exampleEmails, ''] })}
          >
            <Plus aria-hidden /> Add an example
          </Button>
        </div>

        <div className="flex flex-col gap-1.5">
          <Label className="text-sm font-semibold">
            If this reply can’t be sent, the email goes to
          </Label>
          <Select
            value={reply.humanOwner}
            onValueChange={humanOwner => change({ humanOwner })}
          >
            <SelectTrigger
              className="h-11 max-w-80 bg-white"
              aria-label="If this reply can’t be sent, the email goes to"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {props.owners.map(owner => (
                <SelectItem
                  key={owner.value}
                  value={owner.value}
                >
                  {owner.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">Earlier versions</h3>
          <ul className="flex flex-col divide-y divide-zinc-100 rounded-lg border border-zinc-200">
            {props.versions.map(version => (
              <li
                key={version.version}
                className="flex flex-wrap items-center gap-3 px-3 py-2 text-sm"
              >
                <span className="font-medium">Version {version.version}</span>
                <span className="text-zinc-600">
                  {version.version === props.publishedVersion
                    ? `Published ${version.publishedDateTime ? when(version.publishedDateTime) : ''}`
                    : version.status === 'draft'
                      ? `Draft, saved ${when(version.createdDateTime)}`
                      : `Replaced, saved ${when(version.createdDateTime)}`}
                </span>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="ml-auto"
                  disabled={!version.content}
                  onClick={() => props.loadVersion(version)}
                >
                  Open in the editor
                </Button>
              </li>
            ))}
          </ul>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
